import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Copy, Share2, Check } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";

interface PublicAccessLinkProps {
  mfoId: string;
  publicAccessToken: string | null;
  onTokenGenerated: (token: string) => void;
}

const PublicAccessLink = ({ mfoId, publicAccessToken, onTokenGenerated }: PublicAccessLinkProps) => {
  const [isGenerating, setIsGenerating] = useState(false);
  const [isCopied, setIsCopied] = useState(false);
  const { toast } = useToast();

  const generatePublicLink = async () => {
    try {
      setIsGenerating(true);
      
      // Generate a random token
      const token = Math.random().toString(36).substring(2, 15);
      
      // Update the MFO with the new token
      const { error } = await supabase
        .from("mfo_items")
        .update({ public_access_token: token })
        .eq("id", mfoId);
        
      if (error) throw error;
      
      onTokenGenerated(token);
      toast({
        title: "Public link generated",
        description: "A new public access link has been created",
      });
    } catch (error) {
      console.error("Error generating public link:", error);
      toast({
        title: "Error",
        description: "Failed to generate public link",
        variant: "destructive",
      });
    } finally {
      setIsGenerating(false);
    }
  };

  const copyToClipboard = () => {
    if (!publicAccessToken) return;
    
    const publicUrl = `${window.location.origin}/mfo/${mfoId}/public?token=${publicAccessToken}`;
    navigator.clipboard.writeText(publicUrl);
    setIsCopied(true);
    setTimeout(() => setIsCopied(false), 2000);
    
    toast({
      title: "Link copied",
      description: "Public access link copied to clipboard",
    });
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-medium">Public Access Link</h3>
        {!publicAccessToken && (
          <Button
            onClick={generatePublicLink}
            disabled={isGenerating}
            className="flex items-center gap-2"
          >
            <Share2 className="h-4 w-4" />
            {isGenerating ? "Generating..." : "Generate Link"}
          </Button>
        )}
      </div>

      {publicAccessToken && (
        <div className="flex items-center gap-2">
          <Input
            value={`${window.location.origin}/public/mfo/${mfoId}?token=${publicAccessToken}`}
            readOnly
            className="flex-1"
          />
          <Button
            variant="outline"
            size="icon"
            onClick={copyToClipboard}
            className="shrink-0"
          >
            {isCopied ? (
              <Check className="h-4 w-4 text-green-500" />
            ) : (
              <Copy className="h-4 w-4" />
            )}
          </Button>
        </div>
      )}

      <p className="text-sm text-muted-foreground">
        Share this link with team members who don't have system accounts. They will be able to view the MFO details and update task progress.
      </p>
    </div>
  );
};

export default PublicAccessLink; 